import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
// v8.0.0 — Attribution runtime evaluation (additive, best-effort)
import type { ServedProvenance } from '../providerLabel';
import type { ProviderConfidence } from '../types';
import { evaluatePendingForSymbol } from '../../domains/attribution/attributionRuntime';
import { addWatchlistSymbol, removeWatchlistSymbol } from '../../data/watchlistApi';
import {
  WatchlistEntry, DataState, ScoringWeights, JournalEntry, AuditRecord,
  AppSettings, DEFAULT_WEIGHTS, DEFAULT_SETTINGS, DataSource,
  Candle, OptionRow, SessionMeta,
  TradePlan, WaitRecord, JournalDraft, Journal71AEntry,
  TraderHorizon, DEFAULT_HORIZON,
} from '../../types';
// P1-Q4 G-CLIENT-1 (position 8): canonical selection context and advisory
// capability overlay. Pure module — no React, no fetch, no timers.
import {
  CanonicalClientTimeframe, DEFAULT_CANONICAL_TIMEFRAME,
  CapabilityOverlay, CapabilityDiagnostic,
} from '../capabilities/gClient1Capability';

// ─── Per-symbol data (large, stored separately to avoid quota issues) ─────────
const SYMBOL_DATA_KEY = 'af_symbol_data_v2';

function loadSymbolData(): Record<string, Omit<DataState, 'loading' | 'error'>> {
  try {
    const raw = localStorage.getItem(SYMBOL_DATA_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, Omit<DataState, 'loading' | 'error'>>;
    // Sanitize cached candles — values may be strings from older data or API responses
    Object.values(parsed).forEach(sym => {
      if (sym.candles) {
        sym.candles = sym.candles.map(c => ({
          ...c,
          ts:     typeof c.ts     === 'number' ? c.ts     : Number(c.ts),
          open:   typeof c.open   === 'number' ? c.open   : parseFloat(String(c.open)),
          high:   typeof c.high   === 'number' ? c.high   : parseFloat(String(c.high)),
          low:    typeof c.low    === 'number' ? c.low    : parseFloat(String(c.low)),
          close:  typeof c.close  === 'number' ? c.close  : parseFloat(String(c.close)),
          volume: typeof c.volume === 'number' ? c.volume : parseFloat(String(c.volume)) || 0,
        })).filter(c => !isNaN(c.open) && !isNaN(c.low) && c.open > 0);
      }
    });
    return parsed;
  } catch { return {}; }
}

function saveSymbolData(data: Record<string, Omit<DataState, 'loading' | 'error'>>) {
  try { localStorage.setItem(SYMBOL_DATA_KEY, JSON.stringify(data)); } catch {}
}

// ─── Store types ──────────────────────────────────────────────────────────────

interface AppStore {
  // Watchlist
  watchlist: WatchlistEntry[];
  activeSymbol: string | null;

  // Per-symbol data (runtime, partially persisted)
  symbolData: Record<string, DataState>;

  // Settings
  weights: ScoringWeights;
  settings: AppSettings;

  // Phase 3: trader horizon — global per-user intent. Persists locally to
  // localStorage via Zustand persist; the canonical store is Postgres
  // (synced via /api/user/prefs in commits 2.6/2.7).
  horizon: TraderHorizon;

  // P1-Q4 G-CLIENT-1 (position 8): the canonical analysis timeframe. Global and
  // persisted; deliberately NOT symbolData[sym].interval, which remains the
  // separate active-symbol chart-fetch interval. Selecting a canonical value
  // never fetches market data and never rewrites a chart interval.
  canonicalTimeframe: CanonicalClientTimeframe;

  // Advisory capability state. `lastKnownCapabilityOverlay` is the only
  // persisted piece and always rehydrates as LAST_KNOWN, never CURRENT.
  lastKnownCapabilityOverlay: CapabilityOverlay | null;
  currentCapabilityOverlay:   CapabilityOverlay | null;   // in-memory only
  capabilityDiagnostic:       CapabilityDiagnostic | null; // in-memory only

  // Journal
  journal: JournalEntry[];
  auditRecords: AuditRecord[];

  // v5 additions — trade plans and wait records (additive only)
  tradePlans: TradePlan[];
  waitRecords: WaitRecord[];
  // v5.5: Journal drafts — decision-first structured journaling
  journalDrafts:        JournalDraft[];
  activeJournalDraftId: string | null;
  pendingJournalTab:    boolean;  // signal App to switch to journal tab

  // Phase 7.1A: Observability journal (additive, never feeds back into engine)
  journal71aEntries: Journal71AEntry[];

  // Anchored VWAP points per symbol
  anchorPoints: Record<string, number[]>; // symbol -> array of ts (ms)

  // Actions
  addSymbol: (sym: string) => void;
  removeSymbol: (sym: string) => void;
  setWatchlist: (symbols: string[]) => void;
  setActive: (sym: string) => void;

  setCandles: (sym: string, candles: Candle[], source: DataSource) => void;
  setOptions: (sym: string, options: OptionRow[]) => void;
  setMeta: (sym: string, meta: Partial<SessionMeta>) => void;
  /** P3 visible provider trust: record WHICH provider actually served this symbol. */
  setProvenance: (sym: string, provenance: ServedProvenance | null) => void;
  /** P3 confidence penalties: the governed P3-D4 block for this symbol. */
  setProviderConfidence: (sym: string, providerConfidence: ProviderConfidence | null) => void;
  setLoading: (sym: string, loading: boolean) => void;
  setError: (sym: string, error: string | null) => void;
  setInterval: (sym: string, interval: string) => void;

  setWeights: (w: Partial<ScoringWeights>) => void;
  setSettings: (s: Partial<AppSettings>) => void;

  // Phase 3: trader horizon
  setHorizon: (h: TraderHorizon) => void;

  // P1-Q4 G-CLIENT-1: canonical selection and advisory capability actions.
  setCanonicalTimeframe:  (tf: CanonicalClientTimeframe) => void;
  applyCapabilitySuccess: (overlay: CapabilityOverlay) => void;
  applyCapabilityFailure: (diagnostic: CapabilityDiagnostic) => void;

  addAnchor: (sym: string, ts: number) => void;
  removeAnchor: (sym: string, ts: number) => void;
  clearAnchors: (sym: string) => void;

  addJournalEntry: (e: Omit<JournalEntry, 'id'>) => void;
  addAuditRecord: (r: AuditRecord) => void;
  updateAuditRecord: (r: AuditRecord) => void;
  deleteAuditRecord: (id: string) => void;
  clearAuditRecords: () => void;
  updateJournalEntry: (e: JournalEntry) => void;
  deleteJournalEntry: (id: number) => void;

  // v5.5: JournalDraft actions
  createJournalDraft:  (draft: JournalDraft) => void;
  updateJournalDraft:  (id: string, patch: Partial<JournalDraft>) => void;
  deleteJournalDraft:  (id: string) => void;
  setActiveJournalDraft: (id: string | null) => void;
  setPendingJournalTab:  (v: boolean) => void;

  // v5 additive: TradePlan actions
  addTradePlan:    (p: TradePlan)  => void;
  updateTradePlan: (p: TradePlan)  => void;
  deleteTradePlan: (id: string)    => void;

  // v5 additive: WaitRecord actions
  addWaitRecord:    (r: WaitRecord) => void;
  updateWaitRecord: (r: WaitRecord) => void;
  deleteWaitRecord: (id: string)    => void;

  // Phase 7.1A: Observability journal actions
  addJournal71AEntry:    (e: Omit<Journal71AEntry, 'id'>) => void;
  deleteJournal71AEntry: (id: string) => void;
  clearJournal71AEntries: () => void;

  loadSample: (sym: string) => void;
  clearSymbolData: (sym: string) => void;
}

// ─── Sample data builder ──────────────────────────────────────────────────────

function buildSampleCandles(basePrice: number, trend: number, count: number): Candle[] {
  const now = Date.now();
  const startMs = now - count * 5 * 60 * 1000;
  let price = basePrice;
  return Array.from({ length: count }, (_, i) => {
    const noise = (Math.random() - 0.47) * basePrice * 0.005;
    const o = price;
    const c = Math.max(price + trend + noise, price * 0.97);
    const h = Math.max(o, c) + Math.random() * Math.abs(noise) * 0.7;
    const l = Math.min(o, c) - Math.random() * Math.abs(noise) * 0.7;
    price = c;
    return {
      ts: startMs + i * 5 * 60 * 1000,
      open: +o.toFixed(2), high: +h.toFixed(2), low: +l.toFixed(2), close: +c.toFixed(2),
      volume: Math.floor(60000 + Math.random() * 180000)
    };
  });
}

function buildSampleOptions(basePrice: number): OptionRow[] {
  const mag = basePrice < 20 ? 1 : basePrice < 100 ? 5 : basePrice < 500 ? 10 : 50;
  const base = Math.round(basePrice / mag) * mag;
  return Array.from({ length: 13 }, (_, i) => {
    const strike = base + (i - 6) * mag;
    if (strike <= 0) return null;
    const dist = Math.abs(i - 6);
    const callBias = i > 6 ? 1.4 : 0.7;
    const putBias = i < 6 ? 1.4 : 0.7;
    return {
      strike,
      callOI: Math.round((28000 - dist * 2500) * callBias + Math.random() * 4000),
      putOI: Math.round((28000 - dist * 2500) * putBias + Math.random() * 4000),
      gamma: null, callVol: 0, putVol: 0, expiration: null
    };
  }).filter(Boolean) as OptionRow[];
}

const SAMPLE_CONFIGS: Record<string, { price: number; trend: number; meta: SessionMeta }> = {
  MU:   { price: 103.40, trend:  0.016, meta: { prevHigh: 106.00, prevLow: 102.50, pmHigh: 106.50, pmLow: 102.80, prevClose: 103.20, iv: 44 } },
  AMD:  { price: 124.50, trend: -0.018, meta: { prevHigh: 126.50, prevLow: 121.00, pmHigh: 125.50, pmLow: 121.20, prevClose: 125.80, iv: 58 } },
  NVDA: { price: 864.00, trend:  0.060, meta: { prevHigh: 880.00, prevLow: 858.00, pmHigh: 882.00, pmLow: 860.00, prevClose: 862.50, iv: 52 } },
  SPY:  { price: 520.50, trend:  0.012, meta: { prevHigh: 524.50, prevLow: 518.00, pmHigh: 525.00, pmLow: 518.50, prevClose: 519.80, iv: 16 } },
};

function makeDefaultDataState(): DataState {
  return { candles: null, options: null, meta: {}, source: 'sample', lastUpdated: null, error: null, loading: false, interval: '5min' };
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      watchlist: [],
      activeSymbol: null,
      symbolData: {},
      weights: DEFAULT_WEIGHTS,
      settings: DEFAULT_SETTINGS,
      horizon: DEFAULT_HORIZON,   // Phase 3
      // P1-Q4 G-CLIENT-1 — first-use default only. Persisted rehydration
      // overwrites this with the stored preference and never the reverse.
      canonicalTimeframe:         DEFAULT_CANONICAL_TIMEFRAME,
      lastKnownCapabilityOverlay: null,
      currentCapabilityOverlay:   null,
      capabilityDiagnostic:       null,
      journal: [],
      auditRecords: [],
      anchorPoints: {},
      // v5 additive collections
      tradePlans:  [],
      waitRecords: [],
      journalDrafts:        [],
      activeJournalDraftId: null,
      pendingJournalTab:    false,

      // Phase 7.1A: Observability journal
      journal71aEntries: [],

      addSymbol: (sym) => {
        const s = sym.toUpperCase().trim();
        if (!s || get().watchlist.find(w => w.symbol === s)) return;
        const existing = loadSymbolData();
        set(state => ({
          watchlist: [...state.watchlist, { symbol: s, addedAt: Date.now() }],
          activeSymbol: state.activeSymbol || s,
          symbolData: {
            ...state.symbolData,
            [s]: { ...(existing[s] || makeDefaultDataState()), loading: false, error: null }
          }
        }));
        // Backlog Item #10 Phase 3: fire-and-forget server sync. Errors logged
        // but not surfaced -- next add/remove will retry. 409 (duplicate) is
        // treated as success by the API wrapper.
        addWatchlistSymbol(s).catch(err => {
          console.warn('[store] addWatchlistSymbol failed:', err);
        });
      },

      removeSymbol: (sym) => {
        const sym_data = loadSymbolData();
        delete sym_data[sym];
        saveSymbolData(sym_data);
        set(state => {
          const wl = state.watchlist.filter(w => w.symbol !== sym);
          const sd = { ...state.symbolData };
          delete sd[sym];
          return { watchlist: wl, symbolData: sd, activeSymbol: state.activeSymbol === sym ? (wl[0]?.symbol || null) : state.activeSymbol };
        });
        // Backlog Item #10 Phase 3: fire-and-forget server sync. Server is
        // idempotent (200 even if row absent) so this is safe regardless of
        // current server state.
        removeWatchlistSymbol(sym).catch(err => {
          console.warn('[store] removeWatchlistSymbol failed:', err);
        });
      },

      // Backlog Item #10 Phase 2: bulk-replace watchlist (used by server hydration).
      // Preserves symbolData for symbols that survive; drops symbolData for symbols
      // no longer in the new list. NOTE: does NOT call saveWatchlist -- this action
      // is for applying server state, not pushing local changes.
      setWatchlist: (symbols) => {
        const normalized = Array.from(new Set(
          symbols.map(s => String(s || '').trim().toUpperCase()).filter(Boolean)
        ));
        const existing = loadSymbolData();
        set(state => {
          // Build new watchlist entries, preserving addedAt for existing symbols
          const prevBySym: Record<string, number> = {};
          for (const w of state.watchlist) prevBySym[w.symbol] = w.addedAt;
          const now = Date.now();
          const wl: WatchlistEntry[] = normalized.map(s => ({
            symbol:  s,
            addedAt: prevBySym[s] ?? now,
          }));
          // Build new symbolData, only keeping entries for surviving symbols
          const sd: Record<string, DataState> = {};
          for (const s of normalized) {
            sd[s] = state.symbolData[s] ?? { ...(existing[s] || makeDefaultDataState()), loading: false, error: null };
          }
          // Preserve activeSymbol if still in the list, else fall back to first symbol
          const activeSymbol = state.activeSymbol && normalized.includes(state.activeSymbol)
            ? state.activeSymbol
            : (normalized[0] || null);
          return { watchlist: wl, symbolData: sd, activeSymbol };
        });
      },

      setActive: (sym) => set({ activeSymbol: sym }),

      setCandles: (sym, candles, source) => {
        // Sanitize — ensure all OHLCV values are numbers (API responses and localStorage may return strings)
        const sanitized = candles.map(c => ({
          ...c,
          ts:     typeof c.ts     === 'number' ? c.ts     : Number(c.ts),
          open:   typeof c.open   === 'number' ? c.open   : parseFloat(String(c.open)),
          high:   typeof c.high   === 'number' ? c.high   : parseFloat(String(c.high)),
          low:    typeof c.low    === 'number' ? c.low    : parseFloat(String(c.low)),
          close:  typeof c.close  === 'number' ? c.close  : parseFloat(String(c.close)),
          volume: typeof c.volume === 'number' ? c.volume : parseFloat(String(c.volume)) || 0,
        })).filter(c => !isNaN(c.open) && !isNaN(c.low) && c.open > 0);
        set(state => {
          const next = { ...state.symbolData, [sym]: { ...(state.symbolData[sym] || makeDefaultDataState()), candles: sanitized, source, lastUpdated: Date.now(), loading: false, error: null } };
          const saved = loadSymbolData();
          saved[sym] = { ...next[sym], loading: false, error: null } as DataState;
          saveSymbolData(saved);
          return { symbolData: next };
        });
        // v8.0.0 — Attribution: evaluate pending snapshots with new bars (best-effort)
        evaluatePendingForSymbol(sym, sanitized);
      },

      setOptions: (sym, options) => {
        set(state => {
          const next = { ...state.symbolData, [sym]: { ...(state.symbolData[sym] || makeDefaultDataState()), options } };
          const saved = loadSymbolData();
          saved[sym] = { ...next[sym], loading: false, error: null } as DataState;
          saveSymbolData(saved);
          return { symbolData: next };
        });
      },

      setMeta: (sym, meta) => {
        set(state => {
          const existing = state.symbolData[sym] || makeDefaultDataState();
          const next = { ...state.symbolData, [sym]: { ...existing, meta: { ...existing.meta, ...meta } } };
          const saved = loadSymbolData();
          saved[sym] = { ...next[sym], loading: false, error: null } as DataState;
          saveSymbolData(saved);
          return { symbolData: next };
        });
      },

      // Stored as a SIBLING of `meta`, never merged into it: `SessionMeta` is a
      // typed numeric contract (`D-P3T-4`) and provider identity is not one of
      // its fields. `null` is stored as-is so "unknown" stays distinguishable
      // from a stale previous provider.
      setProvenance: (sym, provenance) => set(state => ({
        symbolData: {
          ...state.symbolData,
          [sym]: { ...(state.symbolData[sym] || makeDefaultDataState()), provenance },
        },
      })),

      // Stored verbatim as a sibling of `meta`, exactly like `provenance`.
      // `null` is stored as-is so absence stays distinguishable from a stale
      // previous evaluation.
      setProviderConfidence: (sym, providerConfidence) => set(state => ({
        symbolData: {
          ...state.symbolData,
          [sym]: {
            ...(state.symbolData[sym] || makeDefaultDataState()),
            // The banked contract represents absence by the field being
            // ABSENT, which is deliberately distinct from the governed
            // UNKNOWN state — so `null` is stored as `undefined`, never as
            // a value that could read as a verdict.
            providerConfidence: providerConfidence ?? undefined,
          },
        },
      })),

      setLoading: (sym, loading) => set(state => ({ symbolData: { ...state.symbolData, [sym]: { ...(state.symbolData[sym] || makeDefaultDataState()), loading } } })),
      setError: (sym, error) => set(state => ({ symbolData: { ...state.symbolData, [sym]: { ...(state.symbolData[sym] || makeDefaultDataState()), error, loading: false } } })),
      setInterval: (sym, interval) => set(state => {
        // Phase 3 commit 3.2.2: persist chart bar interval to localStorage.
        // Previously updated in-memory only, so F5 reset interval to whatever
        // was last persisted via setCandles/setOptions/setMeta — confusingly
        // resetting the user's pill selection. Mirrors setOptions/setMeta.
        const next  = { ...state.symbolData, [sym]: { ...(state.symbolData[sym] || makeDefaultDataState()), interval: interval as DataState['interval'] } };
        const saved = loadSymbolData();
        saved[sym]  = { ...next[sym], loading: false, error: null } as DataState;
        saveSymbolData(saved);
        return { symbolData: next };
      }),

      setWeights: (w) => set(state => ({ weights: { ...state.weights, ...w } })),
      setSettings: (s) => set(state => ({ settings: { ...state.settings, ...s } })),

      // Phase 3: trader horizon. Pure local-state update; the API sync
      // (PATCH /api/user/prefs) is performed by the call site via the
      // userPrefsApi wrapper from commit 2.6 — keeps this store action
      // synchronous and side-effect free.
      setHorizon: (h) => set({ horizon: h }),

      // P1-Q4 G-CLIENT-1 — canonical analysis timeframe. Touches nothing else:
      // no active symbol, no chart interval, no market-data fetch, no shadow
      // observation. Persistence is the existing Zustand mechanism only; there
      // is no /api/user/prefs write for this preference at position 8.
      setCanonicalTimeframe: (tf) => set({ canonicalTimeframe: tf }),

      // A validated current payload becomes CURRENT and refreshes the persisted
      // last-known overlay. Nothing else in the store is touched.
      applyCapabilitySuccess: (overlay) => set({
        currentCapabilityOverlay:   overlay,
        lastKnownCapabilityOverlay: overlay,
        capabilityDiagnostic:       null,
      }),

      // A transport or validation failure records a separate diagnostic and
      // leaves any previously validated overlay intact. It never becomes a
      // capability reason code and never rewrites a preference.
      applyCapabilityFailure: (diagnostic) => set({
        currentCapabilityOverlay: null,
        capabilityDiagnostic:     diagnostic,
      }),

      addAnchor: (sym, ts) => set(state => ({
        anchorPoints: { ...state.anchorPoints, [sym]: [...(state.anchorPoints[sym] || []), ts] }
      })),
      removeAnchor: (sym, ts) => set(state => ({
        anchorPoints: { ...state.anchorPoints, [sym]: (state.anchorPoints[sym] || []).filter(t => t !== ts) }
      })),
      clearAnchors: (sym) => set(state => ({ anchorPoints: { ...state.anchorPoints, [sym]: [] } })),

      addJournalEntry: (e) => set(state => ({ journal: [...state.journal, { ...e, id: Date.now() }] })),
      addAuditRecord:    (r) => set(state => ({ auditRecords: [...state.auditRecords, r] })),
      updateAuditRecord: (r) => set(state => ({ auditRecords: state.auditRecords.map(x => x.id === r.id ? r : x) })),
      deleteAuditRecord: (id) => set(state => ({ auditRecords: state.auditRecords.filter(x => x.id !== id) })),
      clearAuditRecords: () => set({ auditRecords: [] }),
      updateJournalEntry: (e) => set(state => ({ journal: state.journal.map(x => x.id === e.id ? e : x) })),
      deleteJournalEntry: (id) => set(state => ({ journal: state.journal.filter(e => e.id !== id) })),

      // v5 additive: TradePlan CRUD
      // v5.5: JournalDraft actions
      createJournalDraft:  (d) => set(state => ({
        journalDrafts: [d, ...state.journalDrafts].slice(0, 100),
        activeJournalDraftId: d.id,
      })),
      updateJournalDraft:  (id, patch) => set(state => ({
        journalDrafts: state.journalDrafts.map(d => d.id === id ? { ...d, ...patch } : d),
      })),
      deleteJournalDraft:  (id) => set(state => ({
        journalDrafts: state.journalDrafts.filter(d => d.id !== id),
        activeJournalDraftId: state.activeJournalDraftId === id ? null : state.activeJournalDraftId,
      })),
      setActiveJournalDraft: (id) => set({ activeJournalDraftId: id }),
      setPendingJournalTab:  (v) => set({ pendingJournalTab: v }),

      addTradePlan:    (p) => set(state => ({ tradePlans: [...state.tradePlans, p] })),
      updateTradePlan: (p) => set(state => ({ tradePlans: state.tradePlans.map(x => x.id === p.id ? p : x) })),
      deleteTradePlan: (id) => set(state => ({ tradePlans: state.tradePlans.filter(x => x.id !== id) })),

      // v5 additive: WaitRecord CRUD
      addWaitRecord:    (r) => set(state => ({ waitRecords: [...state.waitRecords, r] })),
      updateWaitRecord: (r) => set(state => ({ waitRecords: state.waitRecords.map(x => x.id === r.id ? r : x) })),
      deleteWaitRecord: (id) => set(state => ({ waitRecords: state.waitRecords.filter(x => x.id !== id) })),

      // Phase 7.1A: Observability journal actions — never feed back into engine
      addJournal71AEntry: (e) => set(state => ({
        journal71aEntries: [
          { ...e, id: `${e.symbol}_${e.ts}_${Math.random().toString(36).slice(2,7)}` },
          ...state.journal71aEntries,
        ],
      })),
      deleteJournal71AEntry: (id) => set(state => ({
        journal71aEntries: state.journal71aEntries.filter(x => x.id !== id),
      })),
      clearJournal71AEntries: () => set({ journal71aEntries: [] }),

      loadSample: (sym: string) => {
        const s = sym.toUpperCase();
        const cfg = SAMPLE_CONFIGS[s];
        if (!cfg) {
          // Generic sample for any symbol
          const price = 50 + Math.random() * 200;
          const candles = buildSampleCandles(price, (Math.random() - 0.5) * 0.05, 120);
          const options = buildSampleOptions(price);
          get().addSymbol(s);
          get().setCandles(s, candles, 'sample');
          get().setOptions(s, options);
          return;
        }
        const candles = buildSampleCandles(cfg.price, cfg.trend, 120);
        const options = buildSampleOptions(cfg.price);
        get().addSymbol(s);
        get().setCandles(s, candles, 'sample');
        get().setOptions(s, options);
        get().setMeta(s, cfg.meta);
      },

      clearSymbolData: (sym) => {
        const saved = loadSymbolData();
        if (saved[sym]) { saved[sym] = { ...saved[sym], candles: null, options: null } as any; saveSymbolData(saved); }
        set(state => ({ symbolData: { ...state.symbolData, [sym]: makeDefaultDataState() } }));
      },
    }),
    {
      name: 'af_app_state_v2',
      storage: createJSONStorage(() => localStorage),
      // Only persist lightweight state — candles stored separately
      partialize: (state) => ({
        watchlist: state.watchlist,
        activeSymbol: state.activeSymbol,
        weights: state.weights,
        settings: state.settings,
        horizon: state.horizon,   // Phase 3
        // P1-Q4 G-CLIENT-1: the canonical preference and the last validated
        // advisory overlay only. The CURRENT lifecycle label, the transient
        // fetch diagnostic, and any in-flight state are deliberately NOT
        // persisted, so a rehydrated overlay is always LAST_KNOWN.
        canonicalTimeframe:         state.canonicalTimeframe,
        lastKnownCapabilityOverlay: state.lastKnownCapabilityOverlay,
        journal: state.journal,
        auditRecords: state.auditRecords,
        anchorPoints: state.anchorPoints,
        // v5 additive
        tradePlans:  state.tradePlans,
        waitRecords: state.waitRecords,
        // v5.5 additive
        journalDrafts: state.journalDrafts,
        // Phase 7.1A: Observability journal
        journal71aEntries: state.journal71aEntries,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Re-hydrate symbol data from separate storage
        const saved = loadSymbolData();
        const symbolData: Record<string, DataState> = {};
        (state.watchlist || []).forEach((w: WatchlistEntry) => {
          symbolData[w.symbol] = { ...(saved[w.symbol] || makeDefaultDataState()), loading: false, error: null };
        });
        state.symbolData = symbolData;
      }
    }
  )
);
